/**
 * What the Assets panel and the `assets.*` commands do (W2-10 projects-data; DESIGN.md §4.18, §5.13; ARCHITECTURE.md
 * §10). Every change is ONE undoable transaction through the open project's DocStore (Core.doc commands); file bytes
 * go to the content-addressed store first (`persist.putAsset`, idempotent).
 *
 *  - Add: files are sniffed by the io worker (by name when it cannot), images measured; names are sanitised by
 *    Core (ASCII, never "...click.png", never "export.json", a hit sound never exactly tap.mp3 / flick.mp3 /
 *    drag.mp3: such names move into a folder, and the toast says so). Images larger than 2048 px are offered a
 *    downscale before they are stored.
 *  - Replace: every reference moves to the newly chosen file (missing or undecodable files, a new song); the old
 *    file goes when nothing uses it any more. A new song re-validates the song-select preview range and offers to
 *    move it inside the song, in the same step; playback switches to the new song (and back on undo: songSync.ts).
 *  - Remove: an unused file goes at once (undoable); a used one asks first and removes its references (lines go
 *    back to the built-in line image, notes to their normal sound, effects and videos using it are deleted).
 *  - Rename (Core refuses names Phira mishandles, with the reason), Downscale (a smaller copy replaces the image),
 *    Download, Remove unused.
 */

import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { AssetId, AssetMeta, AssetRef, AssetRole } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import type { MessageRef } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import { m } from './assets.messages';
import {
  acceptFor,
  assetRows,
  baseName,
  canDownscale,
  DOWNSCALE_SIDES,
  downscaledPath,
  factsFromName,
  fitsRole,
  formatSec,
  imageMaxSide,
  inferRole,
  LARGE_IMAGE_PX,
  previewClamp,
  ROLE_TITLES,
  usageCount,
  usageText,
  type FileFacts,
} from './assetModel';
import { downloadBlob, pickFiles, readBytes } from './files';
import { isBytesMissing, markPresent, probeAsset } from './presence';
import { playSong } from './songSync';

type Services = AppServices;

/** Injection points for tests (file picker, audio decoding). */
export interface AssetDeps {
  readonly pick?: (accept: string, multiple: boolean) => Promise<File[]>;
  /** Duration of an audio file in seconds, or null when this browser cannot decode it. */
  readonly decodeDuration?: (bytes: Uint8Array) => Promise<number | null>;
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { readonly message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return String(err);
}

/** The file's path below a dropped or picked folder, else its name. */
function fileName(file: File): string {
  const rel = (file as { readonly webkitRelativePath?: string }).webkitRelativePath;
  return rel !== undefined && rel !== '' ? rel : file.name;
}

function storeOf(services: Services): DocStore | null {
  const store = services.store;
  if (store === null) services.ui.toast({ key: m.reasonNoProject }, { kind: 'info' });
  return store;
}

/** One undoable transaction; a refusal shows its reason, notes show as information. */
function runTx(
  services: Services,
  store: DocStore,
  label: MessageRef,
  command: string,
  build: (tx: TxBuilder) => void,
): boolean {
  const out = store.transact({ label, command }, build);
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return false;
  }
  for (const note of out.notes) services.ui.toast(note, { kind: 'info' });
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// File facts
// ---------------------------------------------------------------------------------------------------------------

/** Duration of an audio file through an OfflineAudioContext (null when it cannot be decoded here). */
export async function decodeDurationDefault(bytes: Uint8Array): Promise<number | null> {
  const Ctx = (globalThis as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
  if (Ctx === undefined) return null;
  try {
    const ctx = new Ctx(1, 1, 44_100);
    // decodeAudioData detaches its buffer: decode a copy.
    const buffer = await ctx.decodeAudioData(bytes.slice().buffer);
    return Number.isFinite(buffer.duration) && buffer.duration > 0 ? buffer.duration : null;
  } catch {
    return null;
  }
}

/** Content facts (io worker sniff; the file name when the worker cannot answer). */
async function factsOf(services: Services, bytes: Uint8Array, name: string, browserType: string): Promise<FileFacts> {
  try {
    const sniff = await services.io.call('sniffMedia', { bytes, name });
    if (sniff.kind !== 'other' || sniff.mime !== 'application/octet-stream') {
      return { mime: sniff.mime, codec: sniff.codec, kind: sniff.kind, phiraDecodable: sniff.phiraDecodable };
    }
  } catch {
    // Fall back to the name.
  }
  return factsFromName(name, browserType);
}

/** AssetMeta for a file: codec, decodability, image size and GIF frame delays. */
async function metaOf(
  services: Services,
  bytes: Uint8Array,
  facts: FileFacts,
  durationSec: number | null,
): Promise<AssetMeta> {
  let meta: AssetMeta = { codec: facts.codec, phiraDecodable: facts.phiraDecodable };
  if (durationSec !== null) meta = { ...meta, durationSec };
  if (facts.kind !== 'image') return meta;
  try {
    const info = await services.io.call('imageMeta', { bytes });
    meta = { ...meta, width: info.width, height: info.height };
    if (facts.codec === 'gif') {
      meta = { ...meta, gifFrames: info.frames };
      if (info.gifDelaysMs !== null) meta = { ...meta, gifDelaysMs: info.gifDelaysMs };
      if (info.gifTotalMs !== null) meta = { ...meta, gifTotalMs: info.gifTotalMs };
    }
  } catch {
    // Unknown size: no downscale offer.
  }
  return meta;
}

interface Prepared {
  readonly bytes: Uint8Array;
  readonly facts: FileFacts;
  readonly meta: AssetMeta;
  readonly path: string;
}

/** Offers to downscale a large still image; null = the user cancelled this file. */
async function maybeDownscale(services: Services, p: Prepared): Promise<Prepared | null> {
  const side = imageMaxSide({ meta: p.meta });
  if (p.facts.kind !== 'image' || p.facts.codec === 'gif' || side === null || side <= LARGE_IMAGE_PX) return p;
  const choice = await services.ui.choose<string>({
    title: { key: m.largeTitle, params: { name: p.path, width: p.meta.width ?? 0, height: p.meta.height ?? 0 } },
    body: { key: m.largeBody, params: { limit: LARGE_IMAGE_PX } },
    options: [
      ...DOWNSCALE_SIDES.filter((s) => s < side).map((s) => ({
        value: String(s),
        label: { key: m.downscaleTo, params: { side: s } },
      })),
      { value: 'keep', label: { key: m.keepOriginal }, description: { key: m.keepOriginalHelp } },
    ],
  });
  if (choice === null) return null;
  if (choice === 'keep') return p;
  return downscaleBytes(services, p, Number(choice));
}

async function downscaleBytes(services: Services, p: Prepared, maxSide: number): Promise<Prepared | null> {
  const mime: 'image/png' | 'image/jpeg' = p.facts.mime === 'image/jpeg' ? 'image/jpeg' : 'image/png';
  try {
    const bytes = await services.io.call('downscaleImage', { bytes: p.bytes, maxSide, mime });
    const facts: FileFacts = {
      mime,
      codec: mime === 'image/png' ? 'png' : 'jpeg',
      kind: 'image',
      phiraDecodable: true,
    };
    const meta = await metaOf(services, bytes, facts, null);
    return { bytes, facts, meta, path: downscaledPath(p.path, mime) };
  } catch (err) {
    services.ui.toast({ key: m.downscaleFailed, params: { reason: errorText(err) } }, { kind: 'error' });
    return null;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Add
// ---------------------------------------------------------------------------------------------------------------

/** Adds files to the open chart (one undoable step). Resolves with the ids added. */
export async function addFiles(
  services: Services,
  files: readonly File[],
  role: AssetRole | null,
  deps: AssetDeps = {},
): Promise<AssetId[]> {
  const store = storeOf(services);
  if (store === null || files.length === 0) return [];
  const decode = deps.decodeDuration ?? decodeDurationDefault;
  const refs: AssetRef[] = [];
  for (const file of files) {
    const name = fileName(file);
    let bytes: Uint8Array;
    try {
      bytes = await readBytes(file);
    } catch (err) {
      services.ui.toast({ key: m.readFailed, params: { name, reason: errorText(err) } }, { kind: 'error' });
      continue;
    }
    const facts = await factsOf(services, bytes, name, file.type);
    const fileRole = role ?? inferRole(facts, name);
    if (role !== null && !fitsRole(facts, role)) {
      services.ui.toast(
        { key: m.wrongKind, params: { name, role: services.i18n.t(ROLE_TITLES[role]) } },
        { kind: 'error' },
      );
      continue;
    }
    const duration = facts.kind === 'audio' && fileRole === 'music' ? await decode(bytes) : null;
    const prepared = await maybeDownscale(services, {
      bytes,
      facts,
      meta: await metaOf(services, bytes, facts, duration),
      path: name,
    });
    if (prepared === null) continue;
    try {
      const id = await services.persist.putAsset(prepared.bytes, prepared.facts.mime, fileRole, prepared.meta);
      markPresent(services.persist, id);
      refs.push({
        id,
        path: prepared.path,
        mime: prepared.facts.mime,
        bytes: prepared.bytes.byteLength,
        role: fileRole,
        meta: prepared.meta,
      });
    } catch (err) {
      services.ui.toast({ key: m.storeFailed, params: { name, reason: errorText(err) } }, { kind: 'error' });
    }
  }
  if (refs.length === 0) return [];
  const already = refs.filter((r) => store.doc.assets.has(r.id));
  const fresh = refs.filter((r, i) => !store.doc.assets.has(r.id) && refs.findIndex((x) => x.id === r.id) === i);
  for (const r of already) {
    const existing = store.doc.assets.get(r.id);
    services.ui.toast(
      { key: m.alreadyAdded, params: { name: r.path, path: existing?.path ?? r.path } },
      { kind: 'info' },
    );
  }
  if (fresh.length === 0) return [];
  const label =
    fresh.length === 1 && fresh[0] !== undefined
      ? { key: m.addTxnOne, params: { name: fresh[0].path } }
      : { key: m.addTxnMany, params: { count: fresh.length } };
  const ok = runTx(services, store, label, 'assets.add', (tx) => {
    for (const ref of fresh) Core.doc.addAsset(tx, { ref });
  });
  if (!ok) return [];
  services.ui.toast(
    fresh.length === 1 && fresh[0] !== undefined
      ? { key: m.addedOne, params: { name: store.doc.assets.get(fresh[0].id)?.path ?? fresh[0].path } }
      : { key: m.addedMany, params: { count: fresh.length } },
    { kind: 'success', undo: true },
  );
  return fresh.map((r) => r.id);
}

/** Picks files and adds them. */
export async function pickAndAdd(services: Services, role: AssetRole | null, deps: AssetDeps = {}): Promise<AssetId[]> {
  if (storeOf(services) === null) return [];
  const pick = deps.pick ?? pickFiles;
  const files = await pick(acceptFor(role), true);
  return addFiles(services, files, role, deps);
}

// ---------------------------------------------------------------------------------------------------------------
// Replace
// ---------------------------------------------------------------------------------------------------------------

/**
 * Re-points every reference of `assetId` to a newly chosen file (one undoable step). Resolves with the new id, or
 * null when nothing changed.
 */
export async function replaceAsset(
  services: Services,
  assetId: AssetId,
  deps: AssetDeps = {},
): Promise<AssetId | null> {
  const store = storeOf(services);
  if (store === null) return null;
  const old = store.doc.assets.get(assetId);
  if (old === undefined) {
    services.ui.toast({ key: m.gone }, { kind: 'info' });
    return null;
  }
  const usage = store.idx.assetUsers(assetId);
  const isSong = usage.meta.includes('music');
  const role: AssetRole = isSong ? 'music' : old.role;
  const pick = deps.pick ?? pickFiles;
  const [file] = await pick(acceptFor(role), false);
  if (file === undefined) return null;
  const name = fileName(file);
  let bytes: Uint8Array;
  try {
    bytes = await readBytes(file);
  } catch (err) {
    services.ui.toast({ key: m.readFailed, params: { name, reason: errorText(err) } }, { kind: 'error' });
    return null;
  }
  const facts = await factsOf(services, bytes, name, file.type);
  if (!fitsRole(facts, role)) {
    services.ui.toast(
      { key: m.wrongKind, params: { name, role: services.i18n.t(ROLE_TITLES[role]) } },
      { kind: 'error' },
    );
    return null;
  }
  let duration: number | null = null;
  if (isSong) {
    duration = await (deps.decodeDuration ?? decodeDurationDefault)(bytes);
    if (duration === null) {
      services.ui.toast({ key: m.songUndecodable, params: { name } }, { kind: 'error', durationMs: 10_000 });
      return null;
    }
  }
  const prepared = await maybeDownscale(services, {
    bytes,
    facts,
    meta: await metaOf(services, bytes, facts, duration),
    path: name,
  });
  if (prepared === null) return null;
  // Whether the stored bytes were gone before (UX-067): choosing the same file again then restores them.
  const wasMissing = !(await probeAsset(services.persist, assetId));
  let newId: AssetId;
  try {
    newId = await services.persist.putAsset(prepared.bytes, prepared.facts.mime, role, prepared.meta);
  } catch (err) {
    services.ui.toast({ key: m.storeFailed, params: { name, reason: errorText(err) } }, { kind: 'error' });
    return null;
  }
  markPresent(services.persist, newId);
  if (newId === assetId) {
    if (wasMissing) {
      services.ui.toast({ key: isSong ? m.songRestored : m.fileRestored, params: { name } }, { kind: 'success' });
      if (isSong) void playSong(services, newId, prepared.bytes);
      return newId;
    }
    services.ui.toast({ key: m.sameFile, params: { name } }, { kind: 'info' });
    return null;
  }

  // A new song: keep the song-select preview inside it (Phira refuses to open the chart otherwise).
  let clamp: ReturnType<typeof previewClamp> = null;
  if (isSong && duration !== null) {
    const proposal = previewClamp(store.doc.meta, duration);
    if (proposal !== null) {
      const meta = store.doc.meta;
      const choice = await services.ui.choose<'fix' | 'keep'>({
        title: { key: m.previewTitle },
        body: {
          key: m.previewBody,
          params: {
            length: formatSec(duration),
            start: formatSec(meta.previewStart),
            end: formatSec(meta.previewEnd ?? meta.previewStart + 15),
          },
        },
        options: [
          {
            value: 'fix',
            label: { key: m.previewFix },
            description: {
              key: m.previewFixHelp,
              params: {
                start: formatSec(proposal.previewStart),
                end: formatSec(proposal.previewEnd ?? proposal.previewStart + 15),
              },
            },
          },
          { value: 'keep', label: { key: m.previewKeep }, description: { key: m.previewKeepHelp } },
        ],
      });
      if (choice === null) return null;
      if (choice === 'fix') clamp = proposal;
    }
  }

  const ref: AssetRef = {
    id: newId,
    path: prepared.path,
    mime: prepared.facts.mime,
    bytes: prepared.bytes.byteLength,
    role,
    meta: prepared.meta,
  };
  const ok = runTx(
    services,
    store,
    { key: m.replaceTxn, params: { from: old.path, to: prepared.path } },
    'assets.replace',
    (tx) => {
      Core.doc.addAsset(tx, { ref });
      Core.doc.replaceAsset(tx, { from: assetId, to: newId });
      const left = tx.doc.assets.get(assetId);
      if (left !== undefined && usageCount(tx.idx.assetUsers(assetId)) === 0) {
        Core.doc.removeAsset(tx, { id: assetId, references: 'fail' });
      }
      if (clamp !== null)
        Core.doc.patchMeta(tx, { patch: { previewStart: clamp.previewStart, previewEnd: clamp.previewEnd } });
    },
  );
  if (!ok) return null;
  services.ui.toast(
    { key: m.replaced, params: { from: old.path, to: store.doc.assets.get(newId)?.path ?? prepared.path } },
    { kind: 'success', undo: true },
  );
  if (isSong) void playSong(services, newId, prepared.bytes);
  return newId;
}

// ---------------------------------------------------------------------------------------------------------------
// Remove, rename, downscale, download
// ---------------------------------------------------------------------------------------------------------------

/** Removes a file: at once when unused, after asking when used (its references go too). */
export async function removeAsset(services: Services, assetId: AssetId): Promise<boolean> {
  const store = storeOf(services);
  if (store === null) return false;
  const ref = store.doc.assets.get(assetId);
  if (ref === undefined) return false;
  const usage = store.idx.assetUsers(assetId);
  const uses = usageCount(usage);
  if (uses > 0) {
    const text = { locale: services.i18n.locale, t: services.i18n.t.bind(services.i18n) };
    const ok = await services.ui.confirm({
      title: { key: m.removeUsedTitle, params: { name: ref.path } },
      body: {
        key: usage.meta.includes('music') ? m.removeSongBody : m.removeUsedBody,
        params: { users: usageText(text, usage) },
      },
      confirm: { key: m.removeConfirm },
      danger: true,
    });
    if (!ok) return false;
    // The document may have changed while the question was open.
    if (!store.doc.assets.has(assetId)) return false;
  }
  const ok = runTx(services, store, { key: m.removeTxn, params: { name: ref.path } }, 'assets.remove', (tx) => {
    Core.doc.removeAsset(tx, { id: assetId, references: uses > 0 ? 'remove' : 'fail' });
  });
  if (ok) services.ui.toast({ key: m.removed, params: { name: ref.path } }, { kind: 'success', undo: true });
  return ok;
}

/** Removes every file nothing uses (one undoable step, asks first). */
export async function removeUnused(services: Services): Promise<number> {
  const store = storeOf(services);
  if (store === null) return 0;
  const unused = assetRows(store.doc, store.idx).filter((r) => r.uses === 0);
  if (unused.length === 0) {
    services.ui.toast({ key: m.noneUnused }, { kind: 'info' });
    return 0;
  }
  const ok = await services.ui.confirm({
    title: { key: m.removeUnusedTitle, params: { count: unused.length } },
    body: { key: m.removeUnusedBody },
    confirm: { key: m.removeConfirm },
  });
  if (!ok) return 0;
  const ids = unused.map((r) => r.ref.id).filter((id) => store.doc.assets.has(id));
  const done = runTx(
    services,
    store,
    { key: m.removeUnusedTxn, params: { count: ids.length } },
    'assets.removeUnused',
    (tx) => {
      for (const id of ids) {
        if (usageCount(tx.idx.assetUsers(id)) === 0) Core.doc.removeAsset(tx, { id, references: 'fail' });
      }
    },
  );
  if (!done) return 0;
  services.ui.toast({ key: m.removedUnused, params: { count: ids.length } }, { kind: 'success', undo: true });
  return ids.length;
}

/** Renames a file's package path (Core refuses names Phira mishandles, with the reason). */
export function renameAsset(services: Services, assetId: AssetId, path: string): boolean {
  const store = storeOf(services);
  if (store === null) return false;
  const ref = store.doc.assets.get(assetId);
  const next = path.trim();
  if (ref === undefined || next === ref.path) return false;
  const ok = runTx(
    services,
    store,
    { key: m.renameTxn, params: { from: ref.path, to: next } },
    'assets.rename',
    (tx) => {
      Core.doc.renameAsset(tx, { id: assetId, path: next });
    },
  );
  if (ok) services.ui.toast({ key: m.renamed, params: { name: next } }, { kind: 'success', undo: true });
  return ok;
}

/** Replaces a large still image by a smaller copy (one undoable step; the copy keeps the name). */
export async function downscaleAsset(services: Services, assetId: AssetId, maxSide?: number): Promise<AssetId | null> {
  const store = storeOf(services);
  if (store === null) return null;
  const ref = store.doc.assets.get(assetId);
  if (ref === undefined) return null;
  if (!canDownscale(ref)) {
    services.ui.toast({ key: m.notLarge, params: { name: ref.path, limit: LARGE_IMAGE_PX } }, { kind: 'info' });
    return null;
  }
  const side = imageMaxSide(ref) ?? 0;
  let target = maxSide;
  if (target === undefined) {
    const choice = await services.ui.choose<string>({
      title: {
        key: m.largeTitle,
        params: { name: ref.path, width: ref.meta?.width ?? 0, height: ref.meta?.height ?? 0 },
      },
      body: { key: m.largeBody, params: { limit: LARGE_IMAGE_PX } },
      options: DOWNSCALE_SIDES.filter((s) => s < side).map((s) => ({
        value: String(s),
        label: { key: m.downscaleTo, params: { side: s } },
      })),
    });
    if (choice === null) return null;
    target = Number(choice);
  }
  let bytes: Uint8Array;
  try {
    bytes = await services.persist.getAsset(assetId);
  } catch (err) {
    services.ui.toast({ key: m.readFailed, params: { name: ref.path, reason: errorText(err) } }, { kind: 'error' });
    return null;
  }
  const facts: FileFacts = {
    mime: ref.mime,
    codec: ref.meta?.codec ?? '',
    kind: 'image',
    phiraDecodable: ref.meta?.phiraDecodable ?? true,
  };
  const prepared = await downscaleBytes(services, { bytes, facts, meta: ref.meta ?? {}, path: ref.path }, target);
  if (prepared === null) return null;
  let newId: AssetId;
  try {
    newId = await services.persist.putAsset(prepared.bytes, prepared.facts.mime, ref.role, prepared.meta);
  } catch (err) {
    services.ui.toast({ key: m.storeFailed, params: { name: ref.path, reason: errorText(err) } }, { kind: 'error' });
    return null;
  }
  markPresent(services.persist, newId);
  const ok = runTx(
    services,
    store,
    { key: m.downscaleTxn, params: { name: ref.path, side: target } },
    'assets.downscale',
    (tx) => {
      Core.doc.addAsset(tx, {
        ref: {
          id: newId,
          path: prepared.path,
          mime: prepared.facts.mime,
          bytes: prepared.bytes.byteLength,
          role: ref.role,
          meta: prepared.meta,
        },
      });
      Core.doc.replaceAsset(tx, { from: assetId, to: newId });
      if (tx.doc.assets.has(assetId) && usageCount(tx.idx.assetUsers(assetId)) === 0) {
        Core.doc.removeAsset(tx, { id: assetId, references: 'fail' });
        // The smaller copy takes the original name when its encoding allows it.
        const added = tx.doc.assets.get(newId);
        if (added !== undefined && added.path !== prepared.path) {
          const free = ![...tx.doc.assets.values()].some((a) => a.id !== newId && a.path === prepared.path);
          if (free) Core.doc.renameAsset(tx, { id: newId, path: prepared.path });
        }
      }
    },
  );
  if (!ok) return null;
  services.ui.toast({ key: m.downscaled, params: { name: ref.path, side: target } }, { kind: 'success', undo: true });
  return newId;
}

/** Saves a file of the chart to the computer. */
export async function downloadAsset(services: Services, assetId: AssetId): Promise<void> {
  const store = storeOf(services);
  if (store === null) return;
  const ref = store.doc.assets.get(assetId);
  if (ref === undefined || ref.missing === true) {
    services.ui.toast({ key: m.missingNoDownload }, { kind: 'info' });
    return;
  }
  try {
    const bytes = await services.persist.getAsset(assetId);
    downloadBlob(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: ref.mime }), baseName(ref.path));
  } catch (err) {
    if (isBytesMissing(err)) {
      void probeAsset(services.persist, assetId);
      services.ui.toast({ key: m.storageMissingOne, params: { name: ref.path } }, { kind: 'error' });
      return;
    }
    services.ui.toast({ key: m.readFailed, params: { name: ref.path, reason: errorText(err) } }, { kind: 'error' });
  }
}
